import { useEffect, useState } from 'react';
import { useReleaseNotesPolling } from '../../hooks/useReleaseNotesPolling';
import { LOCAL_STORAGE_NAMES, RELEASE_NOTES_API_INTERVAL } from '../../constants/constants';
import VersionUpdateModal from '.';
import { getLocalDateAndTime } from '../../constants/utilityFunctions';
import { showToast } from '../utils/toast';
import { useTranslation } from 'react-i18next';
import { UpdateCheckerContainer } from '../../assets/styles/layout/mainLayout.styled';
import { Refresh } from '../icons';
import { Button, Paragraph } from '../Ant';

const AppUpdateChecker = ({
  fromProfile,
  onCloseChecker,
  onLoadingChange,
  onUpdateAvailable,
}: {
  fromProfile?: boolean;
  onCloseChecker?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  onUpdateAvailable?: (show: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { latestReleaseNote, isLoading } = useReleaseNotesPolling(fromProfile);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

  useEffect(() => {
    onLoadingChange?.(isLoading);
    return () => onLoadingChange?.(false);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    onUpdateAvailable?.(showUpdateModal);
  }, [showUpdateModal, onUpdateAvailable]);

  const checkForUpdate = () => {
    const localVersion = localStorage.getItem(LOCAL_STORAGE_NAMES.csiAppVersion);
    if (
      latestReleaseNote?.version &&
      localVersion &&
      isVersionNewer(latestReleaseNote?.version, localVersion)
    ) {
      setShowUpdateModal(true);
    } else if (fromProfile && latestReleaseNote?.version && localVersion) {
      onCloseChecker?.();
      showToast('success', t('common:officeAddinUpToDateMessage'));
    }
  };

  useEffect(() => {
    checkForUpdate();
  }, [latestReleaseNote, fromProfile, onCloseChecker]);

  useEffect(() => {
    if (!fromProfile) {
      const interval = setInterval(() => {
        checkForUpdate();
      }, RELEASE_NOTES_API_INTERVAL);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [latestReleaseNote, fromProfile, onCloseChecker]);

  const handleCloseModal = () => {
    setShowUpdateModal(false);
    onCloseChecker?.();
  };
  const handleUpdateAddin = () => {
    globalThis.location.reload();
  };
  const isVersionNewer = (latest: string, current: string): boolean => {
    const latestParts = latest.replace('-', '.').split('.').map(Number);
    const currentParts = current.replace('-', '.').split('.').map(Number);

    for (let i = 0; i < 4; i++) {
      if ((currentParts[i] ?? 0) > (latestParts[i] ?? 0)) return false;
      if ((currentParts[i] ?? 0) < (latestParts[i] ?? 0)) return true;
    }
    return false;
  };
  return (
    <>
      {showUpdateModal && fromProfile && (
        <VersionUpdateModal
          open={showUpdateModal}
          onClose={handleCloseModal}
          onUpdate={() => handleUpdateAddin()}
          version={latestReleaseNote?.version}
          releaseDate={getLocalDateAndTime(new Date(latestReleaseNote?.releaseDate ?? ''), true)}
        />
      )}
      {showUpdateModal && !fromProfile && (
        <UpdateCheckerContainer gap={10} align="center">
          <Refresh />
          <Paragraph $fontsize="13px" $weight={500} $width="100%">
            {t('common:newUpdateAvailable')}
          </Paragraph>
          <Button onClick={() => handleUpdateAddin()}>{t('common:profile.update')}</Button>
        </UpdateCheckerContainer>
      )}
    </>
  );
};

export default AppUpdateChecker;
